// src/components/AnnouncementListModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAnnouncementsFirestore } from '../hooks/useAnnouncementsFirestore';
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5';
import { useAppContext } from '../contexts/AppContext';
import { useTranslation } from 'react-i18next';

/**
 * 공지사항 리스트 모달 (아코디언 형태)
 * 
 * 기능:
 * - 최신 공지가 맨 위
 * - 이전 공지도 모두 확인 가능
 * - 제목 클릭 시 내용 확장/축소
 * - 읽음 상태 관리
 * - Firestore 연동 (비용 최소화)
 */
const AnnouncementListModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { environment } = useAppContext();
  const {
    allAnnouncements,
    hasAnnouncement,
    loading,
    markAsRead,
    markAllAsRead
  } = useAnnouncementsFirestore(environment);
  const [expandedIds, setExpandedIds] = useState([]);

  // 🎯 [Fix] useEffect는 early return 전에 호출 (React Hooks 규칙)
  // 모달이 열릴 때 모든 공지 읽음 처리
  useEffect(() => {
    if (isOpen && allAnnouncements.length > 0) {
      // 1초 후 모든 공지를 읽음 처리 (사용자가 모달을 본 것으로 간주)
      const timer = setTimeout(() => {
        markAllAsRead();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, allAnnouncements.length, markAllAsRead]);

  // early return은 모든 hooks 다음에
  if (!isOpen || !hasAnnouncement) {
    return null;
  }

  // 제목 클릭 시 토글 + 읽음 처리
  const toggleExpand = (id) => {
    // 확장 토글
    setExpandedIds(prev =>
      prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );

    // 읽음 처리
    markAsRead(id);
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTitle>📢 {t('notice_title')}</HeaderTitle>
          <CloseButton onClick={onClose}>
            <IoClose size={24} />
          </CloseButton>
        </Header>

        <AnnouncementList>
          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>공지사항을 불러오는 중...</LoadingText>
            </LoadingContainer>
          ) : (
            allAnnouncements.map((announcement, index) => {
              const isExpanded = expandedIds.includes(announcement.id);
              const isLatest = index === 0;
              const isUnread = !announcement.isRead;

              return (
                <AnnouncementItem key={announcement.id} $isLatest={isLatest} $isUnread={isUnread}>
                  <ItemHeader onClick={() => toggleExpand(announcement.id)}>
                    <ItemMain>
                      <TitleRow>
                        {isLatest && <NewBadge>NEW</NewBadge>}
                        {isUnread && !isLatest && <UnreadDot />}
                        <ItemTitle $isUnread={isUnread}>{announcement.title}</ItemTitle>
                      </TitleRow>
                      <ItemDate>{announcement.createdAt || announcement.date}</ItemDate>
                    </ItemMain>
                    <ExpandIcon>
                      {isExpanded ? <IoChevronUp size={18} /> : <IoChevronDown size={18} />}
                    </ExpandIcon>
                  </ItemHeader>

                  {isExpanded && (
                    <ItemContent>
                      {announcement.formattedContent.map((line, lineIndex) => (
                        <ContentLine key={lineIndex}>
                          {line || '\u00A0'}
                        </ContentLine>
                      ))}
                    </ItemContent>
                  )}
                </AnnouncementItem>
              );
            })
          )}
        </AnnouncementList>
      </ModalContainer>
    </Overlay>
  );
};

// ============================================================
// Styled Components
// ============================================================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999; /* 🔧 [Fix] z-index 증가 (하단 네비게이션보다 위) */
  padding: 20px;
  
  @media (max-width: 768px) {
    align-items: center;
    padding: 16px 12px;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  
  @media (max-width: 768px) {
    max-height: 90vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const Header = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const AnnouncementList = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 12px;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0a0a0;
  }
`;

const AnnouncementItem = styled.div`
  background: ${props => {
    if (props.$isLatest) return '#f8f9ff';
    if (props.$isUnread) return '#fffbf0';
    return 'white';
  }};
  border: ${props => {
    if (props.$isLatest) return '2px solid #667eea';
    if (props.$isUnread) return '2px solid #ffa500';
    return '1px solid #e0e0e0';
  }};
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemHeader = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  user-select: none;
  
  &:active {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const ItemMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
`;

const NewBadge = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  line-height: 1.4;
  margin-top: 1px;
`;

const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  background: #ff4444;
  border-radius: 50%;
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.2);
    }
  }
`;

const ItemTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.35;
  font-weight: ${props => props.$isUnread ? '700' : '600'};
  color: ${props => props.$isUnread ? '#ff4444' : '#333'};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 0.9rem;
  color: #999;
`;

const ItemDate = styled.div`
  font-size: 11px;
  line-height: 1.3;
  color: #94A3B8;
  white-space: nowrap;
`;

const ExpandIcon = styled.div`
  color: #999;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  
  ${ItemHeader}:hover & {
    color: #667eea;
  }
`;

const ItemContent = styled.div`
  padding: 8px 12px 12px;
  border-top: 1px solid #f0f0f0;
  animation: expandDown 0.2s ease-out;
  
  @keyframes expandDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentLine = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #475569;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: break-word;
  margin-bottom: 2px;
  
  /* 빈 줄 처리 */
  &:empty::before {
    content: '\\00A0';
  }
  
  /* 이모지가 포함된 줄 강조 */
  &:first-line {
    font-weight: ${props => /^[📍🗺️🎮]/.test(props.children) ? '600' : '400'};
  }
`;

export default AnnouncementListModal;
